import axios from 'axios';
import { logError } from 'utils/logger';
import { isGameCurrent, removePastGames } from '../helpers';

const APIKey = process.env.REACT_APP_WEATHER_API_KEY;
const googleApiKey = process.env.REACT_APP_GOOGLE_API_KEY;

// Setup base url based on url, ie environment
const PROD_API_BASE_URL = process.env.REACT_APP_PROD_API_BASE_URL;
const DEV_API_URL = process.env.REACT_APP_DEV_API_BASE_URL;
const LOCAL_API_URL = 'http://127.0.0.1:8000';

let base_api_url = PROD_API_BASE_URL;

if (['downkount-dev.web.app'].includes(window.location.host)) {
  base_api_url = DEV_API_URL;
} else if (['localhost:3000'].includes(window.location.host)) {
  base_api_url = LOCAL_API_URL;
}

// console.log('[API] Environment detection:', {
//   host: window.location.host,
//   PROD_API_BASE_URL,
//   DEV_API_URL,
//   LOCAL_API_URL,
//   selected_base_api_url: base_api_url,
// });

if (!base_api_url) {
  console.error('[API] ERROR: base_api_url is undefined! API calls will fail.');
  console.error('[API] Make sure environment variables are set correctly.');
  console.error(
    '[API] Expected: REACT_APP_PROD_API_BASE_URL or REACT_APP_DEV_API_BASE_URL'
  );
}

export const api = {
  leagues: {
    getLeagues: () => axios.get(`${base_api_url}/api/league`),
    getCollegeLeagues: () => axios.get(`${base_api_url}/api/getCollegeLeagues`),
  },
  teams: {
    getTeams: (id) => axios.get(`${base_api_url}/api/get_teams/${id}`),
    getTeamAppData: (teamId) =>
      axios.get(`${base_api_url}/api/getAllAppsAndSites`, {
        params: { team_id: teamId },
      }),
  },
  apps: {
    getAppDataBySiteId: (siteId) =>
      axios.get(`${base_api_url}/api/getAllAppsAndSites`, {
        params: { site_id: siteId },
      }),
  },
  events: {
    getAllEvents: () => axios.get(`${base_api_url}/api/custom_event`),
    getSelectedEvent: (id) =>
      axios.get(`${base_api_url}/api/custom_event/${id}`),
    getEventById: (id) => axios.get(`${base_api_url}/api/event/${id}`),
    getByEspnId: async (espnId, teamName) => {
      const response = await axios.get(
        `${base_api_url}/api/event/espnid/${espnId}`,
        {
          params: { team_name: teamName },
        }
      );
      return response;
    },
  },
  games: {
    getSchedule: (id, leagueId) =>
      axios.get(`${base_api_url}/api/get_schedule/${id}`, {
        params: leagueId ? { league_id: leagueId } : undefined,
      }),
    getUpcomingGames: ({ leagueId }) =>
      axios.post(`${base_api_url}/api/getUpcomingEvents`, {
        league_id: leagueId,
      }),
    getCollegeUpcomingEvents: ({ leagueId, teamId }) =>
      axios.post(`${base_api_url}/api/getCollegeUpcomingEvents`, {
        one_league_id: leagueId,
        one_team_id: teamId,
      }),
    getCollegeSchedule: ({ leagueId, teamId }) =>
      axios.post(`${base_api_url}/api/getCollegeSchedule`, {
        one_league_id: leagueId,
        one_team_id: teamId,
      }),
    getTodaysEvents: () => axios.post(`${base_api_url}/api/getTodaysEvents`),
    getLiveEvents: () => axios.post(`${base_api_url}/api/getLiveEvents`),
    getStadiumByLocationId: (locationId) =>
      axios.get(`${base_api_url}/api/getStadiumByLocationID/${locationId}`),
  },
  config: {
    getConfig: (site_url) =>
      axios.post(`${base_api_url}/api/siteInfoByUrl`, site_url, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
  },
  user: {
    loginUser: (data) => {
      const bodyFormData = new FormData();
      bodyFormData.append('email', data.email);
      bodyFormData.append('password', data.password);

      return axios.post(`${base_api_url}/api/login`, bodyFormData);
    },

    loginWithUser: (data) =>
      axios.post(`${base_api_url}/api/registerGoogle`, data),

    loginWithFacebookUser: (data) =>
      axios.post(`${base_api_url}/api/registerFacebook`, data),

    registerUser: (data) => {
      const { email, password } = data;
      const bodyFormData = new FormData();
      bodyFormData.append('email', email);
      bodyFormData.append('password', password);

      return axios.post(`${base_api_url}/api/register`, bodyFormData);
    },

    resetPassword: (data) => {
      const { email } = data;
      const bodyFormData = new FormData();
      bodyFormData.append('email', email);

      return axios.post(`${base_api_url}/api/password/email`, bodyFormData);
    },

    setNewPassword: (data) => {
      const { email, password, confirmPassword, token } = data;
      const bodyFormData = new FormData();
      bodyFormData.append('email', email);
      bodyFormData.append('password', password);
      bodyFormData.append('password_confirmation', confirmPassword);
      bodyFormData.append('token', token);

      return axios.post(`${base_api_url}/api/password/reset`, bodyFormData);
    },
  },
  favorites: {
    getFavoritesTeams: (id) =>
      axios.get(`${base_api_url}/api/favouriteTeam/${id}`),
    addToFavoriteTeams: (data) => {
      const { userId, teamId, teamLabel, league, leagueId } = data;
      const bodyFormData = new FormData();
      bodyFormData.append('userId', userId);
      bodyFormData.append('teamId', teamId);
      bodyFormData.append('teamLabel', teamLabel);
      if (league) bodyFormData.append('league', league);
      if (leagueId) bodyFormData.append('leagueId', leagueId);

      return axios.post(`${base_api_url}/api/favourite`, bodyFormData);
    },
    deleteFavoriteTeam: (data) =>
      axios.get(
        `${base_api_url}/api/favourite_delete/${data.teamId}/${data.userId}`
      ),
  },
  news: {
    getAllNews: () => axios.get(`${base_api_url}/api/news`),
    getAllNewsByTeam: (id) => axios.get(`${base_api_url}/api/news/${id}`),
    getNewsByTeamWithFilter: (teamId, params) =>
      axios.get(`${base_api_url}/api/news/${teamId}/filter`, { params }),
    getAllNewsByLeague: (leagueId) =>
      axios.get(`${base_api_url}/api/news/league/${leagueId}`),
  },
  ai: {
    // Server-side Perplexity proxy - the API key never reaches the browser.
    // Body: { query, team, opponent, league }. Returns { text }; 429 when
    // the server's rate limit is hit.
    gameSearch: (body) =>
      axios.post(`${base_api_url}/api/ai/game-search`, body, {
        timeout: 45000,
      }),
  },
  productAnalytics: {
    // Get all products overview - requires auth (fixed 2026-09-09, was
    // previously public - see docs/OUTSTANDING_DEFERRED_WORK.md)
    getProductsOverview: (token) =>
      axios.get(`${base_api_url}/api/products/overview`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),

    // Get product report - requires auth (fixed 2026-09-09, was previously
    // public - see docs/OUTSTANDING_DEFERRED_WORK.md)
    getProductReport: (token, params) =>
      axios.get(`${base_api_url}/api/products/report`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),

    // Get API credentials
    getApiCredentials: (token) =>
      axios.get(`${base_api_url}/api/api-credentials`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),

    // Create API credential
    createApiCredential: (token, data) =>
      axios.post(`${base_api_url}/api/api-credentials`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),

    // Update API credential
    updateApiCredential: (token, id, data) =>
      axios.put(`${base_api_url}/api/api-credentials/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),

    // Delete API credential
    deleteApiCredential: (token, id) =>
      axios.delete(`${base_api_url}/api/api-credentials/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
  },
};

/** API calls when url parsing after sharing */

export async function getTeamsSchedule(id) {
  try {
    const response = await axios.get(`${base_api_url}/api/get_schedule/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
}

export async function getTeamsList(id) {
  try {
    const response = await axios.get(`${base_api_url}/api/get_teams/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
}

export async function getLeaguesList() {
  try {
    const response = await axios.get(`${base_api_url}/api/league`);
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
}

export async function getWeather({ location }) {
  try {
    const response = await axios.get(
      // days=3, not 5: this app's WeatherAPI.com key/plan silently caps the
      // response at 3 real days regardless of what's requested - confirmed
      // live via direct curl (days=5 requested, exactly 3 forecastday
      // entries returned). Requesting 3 accurately reflects what the plan
      // actually returns; see docs/testing/user-stories/view-weather.md for
      // the full investigation. Raising this requires a paid WeatherAPI
      // plan upgrade first (Starter tier = 7 days), not just this number.
      `https://api.weatherapi.com/v1/forecast.json?key=${APIKey}&q=${location}&days=3&aqi=no&alerts=no`,
      // No default axios timeout means a slow/hung WeatherAPI.com response
      // waits forever - confirmed live this isn't rate-limiting (20 genuine
      // uncached calls returned instantly with no errors), just ordinary
      // third-party latency variance with nothing to fail it fast. 10s is
      // generous headroom over the ~190ms real response time observed.
      // Rejecting here already flows into the existing "Weather forecast
      // ...unavailable" fallback UI (WeatherGameInfo.jsx's !forecastDays
      // branch) via the catch below - no new error state needed.
      { timeout: 10000 }
    );
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
}

export async function getGeoCoordinates({ stadium, region }) {
  try {
    const response = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${stadium}&region=${
        region || 'us'
      }&key=${googleApiKey}`
    );
    return response.data;
  } catch (error) {
    return error;
  }
}

export async function saveFirebaseToken(token) {
  try {
    await axios.post(`${base_api_url}/api/push_notification`, token);
  } catch (error) {
    logError('Failed to send push notification token', error, { token });
  }
}

export async function getChampionshipGames(id) {
  try {
    const response = await axios.get(`${base_api_url}/api/get_teams/${id}`);
    const futureGames = removePastGames(response.data ?? []);
    const currentGame = isGameCurrent({
      games: futureGames,
      isChampionships: true,
    });

    return { data: futureGames, currentGame };
  } catch (error) {
    throw new Error();
  }
}

export default api;
